import React from "react";
import { Link } from "react-router-dom";
import { Download, FileText, Award, CheckCircle2, FileSearch, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";

export default function ResumeTab({ resume }) {
  if (!resume) return null;
  return (
    <div className="space-y-6" data-testid="resume-tab">
      <Link
        to="/resume-builder"
        data-testid="career-resume-checker-banner"
        className="flex items-center justify-between gap-3 glass-panel rounded-2xl p-5 border border-indigo-400/20 hover:border-indigo-400/40 transition-colors group"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-400/10 flex items-center justify-center shrink-0">
            <FileSearch className="w-5 h-5 text-sky-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Try the AI Resume Builder & Score Checker</p>
            <p className="text-xs text-slate-400">Build your resume and get an instant ATS score, JD match & AI tailoring</p>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-indigo-400 shrink-0 group-hover:translate-x-1 transition-transform" />
      </Link>

      <div className="glass-panel rounded-2xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-heading text-xl font-semibold text-white">{resume.name}</h3>
            <p className="text-sm text-indigo-300">{resume.headline}</p>
          </div>
          <a href={`${API}/resume/pdf`} download data-testid="download-resume-pdf-btn" className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
            <Download className="w-4 h-4" /> Download PDF
          </a>
        </div>
        {resume.bio && <p className="text-sm text-slate-400 mt-3">{resume.bio}</p>}
      </div>

      <div className="glass-panel rounded-2xl p-6">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Skills</p>
        <div className="flex flex-wrap gap-2" data-testid="resume-skills">
          {resume.skills.map((s, i) => <span key={i} className="text-xs px-3 py-1.5 rounded-full border border-indigo-400/20 bg-indigo-400/5 text-indigo-300">{s}</span>)}
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-6">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Projects & Hands-on Labs ({resume.challenge_completion_pct}% of 30-Day Challenge)</p>
        {resume.projects.length === 0 && <p className="text-xs text-slate-400">Complete hands-on lab submissions to populate your resume with real projects.</p>}
        <div className="space-y-3">
          {resume.projects.map((p, i) => (
            <div key={i} className="border-b border-white/5 pb-3 last:border-0" data-testid={`resume-project-${i}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-white">{p.title}</p>
                <span className="text-xs font-bold text-emerald-400">{p.score}/100</span>
              </div>
              <p className="text-xs text-slate-400 mt-1 line-clamp-2">{p.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="glass-panel rounded-2xl p-5 flex items-center gap-3">
          <Award className="w-5 h-5 text-[#FBBF24] shrink-0" />
          <div>
            <p className="text-sm text-white font-medium">{resume.achievements_count} Badges Earned</p>
            <p className="text-xs text-slate-400">Level {resume.level_info.level} — {resume.level_info.title}</p>
          </div>
        </div>
        <div className="glass-panel rounded-2xl p-5 flex items-center gap-3">
          <CheckCircle2 className={`w-5 h-5 shrink-0 ${resume.certificate ? "text-emerald-400" : "text-slate-400"}`} />
          <div>
            <p className="text-sm text-white font-medium">{resume.certificate ? `Certified — ${resume.certificate.certificate_id}` : "Not yet certified"}</p>
            <p className="text-xs text-slate-400">Cloudivo</p>
          </div>
        </div>
      </div>
    </div>
  );
}
